import { useMutation, useQueryClient } from '@tanstack/react-query';

import { savePreferencesForScope } from '../api/user-preferences-api';
import {
  type ColorModePreference,
  isColorModePreference,
  PreferenceScope,
  UserPreferenceKey,
  type UserPreferences,
} from '../types';
import { PreferenceBrowserStorage } from '../utils/preference-session-storage';
import { useCanSavePreferences } from './useCanSavePreferences';
import { usePreferenceToast } from './usePreferenceToast';
import { useUserPreferences } from './useUserPreferences';

type PreferencesByScope = Record<PreferenceScope, Partial<UserPreferences>>;

export interface UseColorModePreferenceReturn {
  mode: ColorModePreference;
  setColorMode: (value: ColorModePreference) => void;
}

/**
 * Read + write the global ColorMode ("appearance") preference. The write is
 * optimistic — the browser store and the react-query cache update immediately,
 * with rollback + a toast on failure — so any surface (currently the account
 * menu) can flip the theme in a single call. This is the single home for the
 * appearance control now that it no longer lives in the preferences modal.
 */
export function useColorModePreference(): UseColorModePreferenceReturn {
  const queryClient = useQueryClient();
  const showToast = usePreferenceToast();
  const canSave = useCanSavePreferences();
  const { preferences } = useUserPreferences(PreferenceScope.GLOBAL);
  // Guarded rather than `?? 'system'`: rows still holding the retired 'legacy'
  // string would otherwise surface as an unknown mode, leaving the menu with no
  // option checked and no trailing label.
  const stored: unknown = preferences[UserPreferenceKey.ColorMode];
  const mode: ColorModePreference = isColorModePreference(stored)
    ? stored
    : 'system';

  const { mutate: saveColorMode } = useMutation({
    mutationFn: (value: ColorModePreference) =>
      savePreferencesForScope(PreferenceScope.GLOBAL, {
        [UserPreferenceKey.ColorMode]: value,
      }),
    onMutate: async (value: ColorModePreference) => {
      const previousStored = PreferenceBrowserStorage.get(
        PreferenceScope.GLOBAL
      )[UserPreferenceKey.ColorMode];
      PreferenceBrowserStorage.update(
        PreferenceScope.GLOBAL,
        UserPreferenceKey.ColorMode,
        value
      );
      await queryClient.cancelQueries({ queryKey: ['user-preferences'] });
      const previousByScope = queryClient.getQueryData<PreferencesByScope>([
        'user-preferences',
      ]);
      queryClient.setQueryData<PreferencesByScope>(
        ['user-preferences'],
        (prev) =>
          prev
            ? {
                ...prev,
                [PreferenceScope.GLOBAL]: {
                  ...prev[PreferenceScope.GLOBAL],
                  [UserPreferenceKey.ColorMode]: value,
                },
              }
            : prev
      );
      return { previousStored, previousByScope };
    },
    onError: (error, _value, context) => {
      if (context?.previousByScope) {
        queryClient.setQueryData(['user-preferences'], context.previousByScope);
      }
      if (context?.previousStored === undefined) {
        PreferenceBrowserStorage.remove(
          PreferenceScope.GLOBAL,
          UserPreferenceKey.ColorMode
        );
      } else {
        PreferenceBrowserStorage.update(
          PreferenceScope.GLOBAL,
          UserPreferenceKey.ColorMode,
          context.previousStored
        );
      }
      showToast('Failed to save appearance', 'error', { error });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user-preferences'] });
    },
  });

  // While impersonating the pick applies to this session only.
  const applyForSession = (value: ColorModePreference) =>
    PreferenceBrowserStorage.update(
      PreferenceScope.GLOBAL,
      UserPreferenceKey.ColorMode,
      value
    );

  return { mode, setColorMode: canSave ? saveColorMode : applyForSession };
}
